/**
 * The world map.
 *
 * A different question from the corner map, and answered differently. The
 * corner map is where you are standing and what is within a few dozen paces;
 * this is where that place sits in the world, drawn the way an atlas is: every
 * place there is, whether or not anybody has walked it, with borders round the
 * named parts of it and their names written on.
 *
 * Every region carries a chart, which is a picture of the whole of it at one
 * reading every few tiles. That is what makes this a map rather than a picture
 * of the one region somebody happens to be standing in.
 *
 * It pans by dragging and zooms by scrolling, and it is drawn into a canvas the
 * size of the panel rather than the size of the world: a continent at close
 * zoom would otherwise be a twenty five thousand pixel canvas nobody asked for.
 */

import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { Game } from '../game/game';
import type { Chart } from '@inverse/protocol';
import {
  MARKER_COLOURS,
  MARKER_ICONS,
  MARKER_MAX,
  markerName,
  type MapMarker,
  type MarkerIcon,
  type Waypoint,
} from './map-marks';
import type { PartyWire } from '@inverse/protocol';
import { paintChart } from './map-paint';
import { placesOverlap } from '@inverse/common';
import { CloseButton } from './CloseButton';

/** How far in and out the map will go, in pixels to the tile. */
const CLOSEST = 8;
const FURTHEST = 0.02;

/**
 * How far out it opens: the place you are standing in, filling the frame with a
 * little room round it so its neighbours are in sight.
 */
function opensAt(frameWidth: number, frameHeight: number, regionWidth: number): number {
  const across = Math.max(1, regionWidth) * 1.4;
  return Math.max(FURTHEST, Math.min(CLOSEST, Math.min(frameWidth, frameHeight) / across));
}

/** What a place's border and its name are drawn in. */
const BORDER = 'rgba(223, 234, 236, 0.55)';
const BORDER_LIT = 'rgba(12, 213, 227, 0.95)';

/** The primary colour, which is what marks the person reading the map. */
const YOU = '#0cd5e3';

/** One place on the map, with everything needed to draw and point at it. */
interface Area {
  key: string;
  name: string;
  about: string;
  /** Whether it is a whole region or a named part of one. */
  region: boolean;
  originX: number;
  originZ: number;
  width: number;
  height: number;
  /** Said in full, where there is a hierarchy above it to say. */
  full?: string;
}

/**
 * A named part of a place, worked out of its chart.
 *
 * The continent divides into areas and towns whose borders follow rivers and
 * ridges, so a part of the map is a set of blocks rather than a rectangle. What
 * is kept here is what drawing needs: where to write the name, how much ground
 * it covers, and how far the writing may spread.
 */
interface Part {
  key: string;
  /** Which of the chart's areas this is, counted from one. */
  held: number;
  name: string;
  about: string;
  /** Said in full: North Kaelen, The Ashen Slopes. */
  full: string;
  town: boolean;
  /** Where the name is written, in world tiles. */
  x: number;
  z: number;
  /** How much ground it covers, in tiles, and how wide it is across the middle. */
  covers: number;
  across: number;
}

/**
 * The parts of one place, read off its chart.
 *
 * Worked out once per chart and kept, because it is a walk over every reading
 * and the answer only changes when the chart does.
 */
function partsOf(id: string, chart: Chart, originX: number, originZ: number): Part[] {
  const sumX = new Float64Array(chart.areas.length + 1);
  const sumZ = new Float64Array(chart.areas.length + 1);
  const count = new Float64Array(chart.areas.length + 1);
  const left = new Float64Array(chart.areas.length + 1).fill(Number.POSITIVE_INFINITY);
  const right = new Float64Array(chart.areas.length + 1).fill(Number.NEGATIVE_INFINITY);

  for (let bz = 0; bz < chart.height; bz++) {
    for (let bx = 0; bx < chart.width; bx++) {
      const held = chart.area[bz * chart.width + bx] ?? 0;
      if (held === 0 || held > chart.areas.length) continue;

      sumX[held] = (sumX[held] ?? 0) + bx;
      sumZ[held] = (sumZ[held] ?? 0) + bz;
      count[held] = (count[held] ?? 0) + 1;
      left[held] = Math.min(left[held] ?? Number.POSITIVE_INFINITY, bx);
      right[held] = Math.max(right[held] ?? Number.NEGATIVE_INFINITY, bx);
    }
  }

  const parts: Part[] = [];

  for (let held = 1; held <= chart.areas.length; held++) {
    const blocks = count[held] ?? 0;
    if (blocks === 0) continue;

    const area = chart.areas[held - 1];
    if (!area) continue;

    const part = [area.part, area.region].filter(Boolean).join(' ');

    parts.push({
      key: `${id}:${held}`,
      held,
      name: area.name,
      about: area.about ?? '',
      full: part ? `${part}, ${area.name}` : area.name,
      town: area.town === true,
      x: originX + ((sumX[held] ?? 0) / blocks + 0.5) * chart.step,
      z: originZ + ((sumZ[held] ?? 0) / blocks + 0.5) * chart.step,
      covers: blocks * chart.step * chart.step,
      across: Math.max(1, (right[held] ?? 0) - (left[held] ?? 0) + 1) * chart.step,
    });
  }

  return parts;
}

/**
 * What a layer is called where somebody reads it.
 *
 * The ground everybody walks on is the world itself; below it is counted,
 * because there is no reason for there to be only one cellar.
 */
function layerName(layer: number): string {
  if (layer === 0) return 'Versa';
  if (layer < 0) return `Underground ${Math.abs(layer)}`;

  return `Upper ${layer}`;
}

/**
 * Where the middle of the picture is, kept over the ground there is.
 *
 * A map opens on the reader, and the reader is often standing on a coast, which
 * would leave half the panel as empty ocean and the other half as nothing at
 * all. Two bounds are kept: the place being stood in, which is what should fill
 * the frame when the map is opened, and everywhere there is, which is as far as
 * a drag may go. Without the second a dungeon in another corner of the world
 * would drag the picture off the continent; without the first there would be
 * nothing to stop somebody dragging into the void and reading a black square.
 */
function centreOn(
  wanted: { x: number; z: number },
  here: { originX: number; originZ: number; width: number; height: number } | undefined,
  frameWidth: number,
  frameHeight: number,
  perTile: number,
): { x: number; z: number } {
  if (!here) return wanted;

  // A little sea round the outside, so a coast is not flush with the frame.
  const margin = 48;
  const minX = here.originX - margin;
  const minZ = here.originZ - margin;
  const maxX = here.originX + here.width + margin;
  const maxZ = here.originZ + here.height + margin;

  const halfWide = frameWidth / 2 / perTile;
  const halfTall = frameHeight / 2 / perTile;

  return {
    x:
      maxX - minX <= halfWide * 2
        ? (minX + maxX) / 2
        : Math.min(Math.max(wanted.x, minX + halfWide), maxX - halfWide),
    z:
      maxZ - minZ <= halfTall * 2
        ? (minZ + maxZ) / 2
        : Math.min(Math.max(wanted.z, minZ + halfTall), maxZ - halfTall),
  };
}

export function WorldMap({
  game,
  arranging = false,
  openOn = null,
  openOnLayer = null,
  marks,
  party,
  onMarks,
  onMove,
  onClose,
}: {
  game: RefObject<Game | null>;
  /** Where somebody is headed, and the places they have written down. */
  marks: { waypoint: Waypoint | null; markers: MapMarker[] };
  /** Who is going with them, drawn on the map beside their own marks. */
  party: PartyWire;
  onMarks: (marks: { waypoint: Waypoint | null; markers: MapMarker[] }) => void;
  /**
   * Whether regions can be picked up and put somewhere else.
   *
   * Only while the world is being edited. This is what moving an island is: an
   * island is a region, and where a region sits is a number somebody used to
   * have to type.
   */
  arranging?: boolean;
  /**
   * A place to open on, so arranging one starts by looking at it.
   *
   * Pressing Move It beside a place in the tools opens the map here rather than
   * wherever the map happened to be left, which on a continent is nowhere near
   * the thing being moved.
   */
  openOn?: string | null;
  /** Which layer that place is on, so the map opens showing it. */
  openOnLayer?: number | null;
  onMove?: (region: string, tx: number, tz: number) => void;
  onClose: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  /** Where the middle of the picture is, in the world's own tiles. */
  const [at, setAt] = useState<{ x: number; z: number } | null>(null);
  /** Pixels to the tile. */
  const [scale, setScale] = useState(0);

  /**
   * Which layer is being read, or nothing for the one being stood on.
   *
   * A map of everything at once would draw a dungeon over the town it is under,
   * which is a picture of neither.
   */
  const [layer, setLayer] = useState<number | null>(null);
  const [standingOn, setStandingOn] = useState(0);

  const [named, setNamed] = useState('');
  const [where, setWhere] = useState('');

  /**
   * The little menu a right click opens, and where it opened.
   *
   * Kept in the map rather than in the game's own menu, because what it offers
   * is about the map: where to go, and what to write down.
   */
  const [menu, setMenu] = useState<{ x: number; y: number; tx: number; tz: number } | null>(null);

  /** Whether the list of markers is up, which is where they are renamed and deleted. */
  const [managing, setManaging] = useState(false);

  /** What the pointer is over, and where to put the label for it. */
  const [over, setOver] = useState<{ area: Area; x: number; y: number } | null>(null);

  const [dragging, setDragging] = useState<{
    id: string;
    originX: number;
    originZ: number;
    width: number;
    height: number;
    clash: boolean;
  } | null>(null);

  /** Pictures of each place, kept so the map is not redrawn from readings every frame. */
  const pictures = useRef(new Map<string, HTMLCanvasElement>());

  /** The named parts of each place, worked out of its chart once. */
  const parts = useRef(new Map<string, Part[]>());

  /** The charts on the layer being read, so the pointer can be told what it is over. */
  const charted = useRef<{ id: string; originX: number; originZ: number; chart: Chart }[]>([]);

  /** What is drawn where, so the pointer can be told what it is over. */
  const areas = useRef<Area[]>([]);

  /**
   * Which layer to draw, when nobody has said.
   *
   * Opened to arrange a place, it is that place's layer rather than the one
   * being stood on. The Hollow Deep is a cellar and everybody arranging it is
   * standing in the daylight above it, so the map drew the overworld, the
   * dungeon was on another layer and therefore not drawn, and there was nothing
   * on screen to pick up. The map follows what it was opened for.
   */
  const showing = layer ?? openOnLayer ?? standingOn;

  /**
   * The middle of the picture, held over the ground there is.
   *
   * Everything that reads the middle reads it through here: drawing, pointing,
   * dragging and zooming all have to agree about where the map is, or a click
   * lands somewhere other than where it looks.
   */
  const centreFor = (
    view: NonNullable<ReturnType<Game['mapView']>>,
    frameWidth: number,
    frameHeight: number,
    perTile: number,
  ): { x: number; z: number } => {
    // Held inside the place being stood in until somebody drags, and inside
    // everywhere there is afterwards, so the map opens on the continent and can
    // still be taken anywhere in the world.
    const here = view.plan.find((one) => one.id === view.region.id) ?? view.region;

    if (!at) {
      // Opened to arrange one place, the map opens on that place rather than on
      // wherever somebody happens to be standing, which on a continent is
      // usually nowhere near it.
      const wanted = openOn ? view.plan.find((one) => one.id === openOn) : undefined;
      const middle = wanted
        ? { x: wanted.originX + wanted.width / 2, z: wanted.originZ + wanted.height / 2 }
        : { x: view.where.x, z: view.where.z };

      return centreOn(middle, wanted ?? here, frameWidth, frameHeight, perTile);
    }

    const world = (view.plan.length > 0 ? view.plan : [view.region]).filter(
      (one) => ('layer' in one ? (one.layer ?? 0) : 0) === showing,
    );

    let minX = Number.POSITIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;

    for (const place of world) {
      minX = Math.min(minX, place.originX);
      minZ = Math.min(minZ, place.originZ);
      maxX = Math.max(maxX, place.originX + place.width);
      maxZ = Math.max(maxZ, place.originZ + place.height);
    }

    if (!Number.isFinite(minX)) return at;

    return centreOn(
      at,
      { originX: minX, originZ: minZ, width: maxX - minX, height: maxZ - minZ },
      frameWidth,
      frameHeight,
      perTile,
    );
  };

  // ---------------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------------

  const draw = (): void => {
    const canvas = canvasRef.current;
    const frame = frameRef.current;
    const view = game.current?.mapView();
    if (!canvas || !frame || !view) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const box = frame.getBoundingClientRect();
    const width = Math.max(1, Math.floor(box.width));
    const height = Math.max(1, Math.floor(box.height));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const regions =
      view.plan.length > 0
        ? view.plan
        : [
            {
              id: view.region.id,
              name: view.region.name,
              originX: view.region.originX,
              originZ: view.region.originZ,
              width: view.region.width,
              height: view.region.height,
              layer: 0,
            },
          ];

    const here = regions.find((one) => one.id === view.region.id);
    const onLayer = regions.filter((one) => (one.layer ?? 0) === showing);

    // Opened on the reader, at whatever shows the place they are in, and held
    // over the world rather than beside it.
    const perTile = scale > 0 ? scale : opensAt(width, height, view.region.width);
    const middle = centreFor(view, width, height, perTile);

    const toScreen = (x: number, z: number): { x: number; y: number } => ({
      x: (x - middle.x) * perTile + width / 2,
      y: (z - middle.z) * perTile + height / 2,
    });

    context.imageSmoothingEnabled = false;
    context.fillStyle = '#07171a';
    context.fillRect(0, 0, width, height);

    const found: Area[] = [];

    // Biggest first, so a small place lands on top of the one it sits inside
    // rather than under it, and so the one underneath is the only one allowed to
    // paint its own ocean.
    const stacked = [...onLayer].sort(
      (one, other) => other.width * other.height - one.width * one.height,
    );
    const widest = stacked[0]?.id ?? '';

    for (const place of stacked) {
      if (place.width <= 0 || place.height <= 0) continue;

      const corner = toScreen(place.originX, place.originZ);
      const w = place.width * perTile;
      const h = place.height * perTile;

      // Off the edge of the panel: nothing to draw, and nothing to point at.
      if (corner.x > width || corner.y > height || corner.x + w < 0 || corner.y + h < 0) continue;

      // Every place is drawn from its chart, including the one being stood in:
      // a picture of four million tiles at full detail is not something to
      // build while somebody is dragging the map about.
      let picture = pictures.current.get(place.id);
      if (!picture) {
        const chart = view.charts[place.id];
        if (chart) {
          // A place standing inside another one leaves its open sea clear, so
          // the water it is actually in shows through rather than a rectangle
          // of its own water sitting on top.
          picture = paintChart(chart, place.id !== widest);
          pictures.current.set(place.id, picture);
        }
      }

      if (picture) {
        // The chart, and nothing else. There used to be a square of the ground
        // that had actually arrived drawn over the top of it, at a detail the
        // chart could not match, which meant the map had one sharp square around
        // the reader and rough colour everywhere else. The chart is fine enough
        // to be the map now, so the whole map is at one detail.
        context.drawImage(picture, corner.x, corner.y, w, h);
      } else {
        // No chart yet: the shape of the place, so at least it is on the map.
        context.fillStyle = 'rgba(12, 213, 227, 0.08)';
        context.fillRect(corner.x, corner.y, w, h);
      }

      found.push({
        key: place.id,
        name: place.name,
        about: '',
        region: true,
        originX: place.originX,
        originZ: place.originZ,
        width: place.width,
        height: place.height,
      });
    }

    // The named parts of every place, drawn from its chart: a border wherever
    // the area changes between one reading and the next, and the name written
    // inside it. This is the whole point of the areas, and why the continent no
    // longer has boxes drawn on it: a border follows the river it runs along.
    const shown: { id: string; originX: number; originZ: number; chart: Chart }[] = [];
    const labels: { part: Part; region: string }[] = [];

    for (const place of onLayer) {
      const chart = view.charts[place.id];
      if (!chart || chart.areas.length === 0) continue;

      shown.push({ id: place.id, originX: place.originX, originZ: place.originZ, chart });

      let held = parts.current.get(place.id);
      if (!held) {
        held = partsOf(place.id, chart, place.originX, place.originZ);
        parts.current.set(place.id, held);
      }

      const step = chart.step * perTile;
      if (step > 0.35) {
        const lit = over?.area.key.split(':')[0] === place.id ? over.area.key : '';

        context.lineWidth = 1;
        context.strokeStyle = BORDER;
        context.beginPath();

        for (let bz = 0; bz < chart.height; bz++) {
          for (let bx = 0; bx < chart.width; bx++) {
            const mine = chart.area[bz * chart.width + bx] ?? 0;
            if (mine === 0) continue;

            const east = bx + 1 < chart.width ? (chart.area[bz * chart.width + bx + 1] ?? 0) : 0;
            const south =
              bz + 1 < chart.height ? (chart.area[(bz + 1) * chart.width + bx] ?? 0) : 0;

            // Only against ground that is in some other area: the coast draws
            // itself, and outlining it as well turns every island into a sticker.
            if (east !== mine && east !== 0) {
              const corner = toScreen(
                place.originX + (bx + 1) * chart.step,
                place.originZ + bz * chart.step,
              );
              context.moveTo(corner.x, corner.y);
              context.lineTo(corner.x, corner.y + step);
            }

            if (south !== mine && south !== 0) {
              const corner = toScreen(
                place.originX + bx * chart.step,
                place.originZ + (bz + 1) * chart.step,
              );
              context.moveTo(corner.x, corner.y);
              context.lineTo(corner.x + step, corner.y);
            }
          }
        }

        context.stroke();

        // The one under the pointer again, brighter and over the top, so
        // pointing at a place shows how far it reaches.
        if (lit) {
          const under = Number(lit.split(':')[1] ?? 0);
          context.lineWidth = 2;
          context.strokeStyle = BORDER_LIT;
          context.beginPath();

          for (let bz = 0; bz < chart.height; bz++) {
            for (let bx = 0; bx < chart.width; bx++) {
              const mine = chart.area[bz * chart.width + bx] ?? 0;
              const east = bx + 1 < chart.width ? (chart.area[bz * chart.width + bx + 1] ?? 0) : 0;
              const south =
                bz + 1 < chart.height ? (chart.area[(bz + 1) * chart.width + bx] ?? 0) : 0;

              if (mine !== east && (mine === under || east === under)) {
                const corner = toScreen(
                  place.originX + (bx + 1) * chart.step,
                  place.originZ + bz * chart.step,
                );
                context.moveTo(corner.x, corner.y);
                context.lineTo(corner.x, corner.y + step);
              }

              if (mine !== south && (mine === under || south === under)) {
                const corner = toScreen(
                  place.originX + bx * chart.step,
                  place.originZ + (bz + 1) * chart.step,
                );
                context.moveTo(corner.x, corner.y);
                context.lineTo(corner.x + step, corner.y);
              }
            }
          }

          context.stroke();
        }
      }

      for (const part of held) labels.push({ part, region: place.id });
    }

    charted.current = shown;

    // Ground with a name drawn round it, in places that have no areas of their
    // own: the tutorial island and anything else hand made. A place that has
    // areas has borders that mean something, and a box over the top of them
    // would only be an older, worse answer to the same question.
    if (showing === (here?.layer ?? 0) && (view.charts[view.region.id]?.areas.length ?? 0) === 0) {
      for (const place of view.region.places) {
        if (place.plane !== 0) continue;

        found.push({
          key: `${view.region.id}:${place.id}`,
          name: place.name,
          about: place.about,
          region: false,
          originX: view.region.originX + place.tx,
          originZ: view.region.originZ + place.tz,
          width: place.width,
          height: place.height,
        });
      }
    }

    // Borders last, over the ground, largest first so a district drawn inside a
    // county is drawn on top of it.
    const bordered = found
      .filter((area) => !area.region)
      .sort((one, other) => other.width * other.height - one.width * one.height);

    for (const area of bordered) {
      const corner = toScreen(area.originX, area.originZ);
      const w = area.width * perTile;
      const h = area.height * perTile;
      if (corner.x > width || corner.y > height || corner.x + w < 0 || corner.y + h < 0) continue;

      const lit = over?.area.key === area.key;
      context.strokeStyle = lit ? BORDER_LIT : BORDER;
      context.lineWidth = lit ? 2 : 1;
      context.setLineDash(lit ? [] : [5, 4]);
      context.strokeRect(corner.x, corner.y, w, h);
      context.setLineDash([]);

      // A name only where there is room for one, so a map zoomed out is a map
      // rather than a heap of words.
      if (w > 46 && h > 16) {
        context.fillStyle = lit ? '#eaf6f8' : '#cfe2e6';
        context.font = `${lit ? '600' : '500'} ${Math.max(10, Math.min(15, w / 9))}px system-ui, sans-serif`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(area.name, corner.x + w / 2, corner.y + h / 2, w - 8);
      }
    }

    // And the names, over their borders: the kingdoms first, faint and large,
    // then the areas, then the towns, which are the smallest things on the map
    // and the ones anybody is actually looking for.
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // What has been written already, so nothing is written over anything else.
    // A map that overlaps its own labels is a map somebody has to squint at.
    const taken: { x: number; y: number; w: number; h: number }[] = [];

    const room = (x: number, y: number, w: number, h: number): boolean => {
      for (const held of taken) {
        if (Math.abs(x - held.x) * 2 < w + held.w && Math.abs(y - held.y) * 2 < h + held.h) {
          return false;
        }
      }

      taken.push({ x, y, w, h });
      return true;
    };

    // The kingdoms: one name over the whole of each third, drawn faintly and
    // spread out, the way an atlas writes the name of a country.
    const kingdoms = new Map<string, { x: number; z: number; covers: number }>();

    for (const { part } of labels) {
      const area = part.full.split(', ')[0]?.split(' ').slice(1).join(' ') ?? '';
      if (!area) continue;

      const held = kingdoms.get(area) ?? { x: 0, z: 0, covers: 0 };
      held.x += part.x * part.covers;
      held.z += part.z * part.covers;
      held.covers += part.covers;
      kingdoms.set(area, held);
    }

    for (const [name, held] of kingdoms) {
      if (held.covers <= 0) continue;

      const middleOf = toScreen(held.x / held.covers, held.z / held.covers);
      const size = Math.max(13, Math.min(34, Math.sqrt(held.covers) * perTile * 0.06));
      if (size < 13) continue;

      context.font = `600 ${size}px system-ui, sans-serif`;
      context.letterSpacing = `${Math.round(size * 0.22)}px`;
      context.fillStyle = 'rgba(233, 246, 248, 0.24)';
      context.fillText(name.toUpperCase(), middleOf.x, middleOf.y, held.covers ** 0.5 * perTile);
      context.letterSpacing = '0px';
    }

    // The towns first, because a town is what somebody is looking for on a map,
    // and then the country largest first, so the county gets the room and the
    // parish gives way rather than the other way about.
    const written = [...labels].sort(
      (one, other) =>
        Number(other.part.town) - Number(one.part.town) || other.part.covers - one.part.covers,
    );

    for (const { part } of written) {
      const across = part.across * perTile;
      if (part.town ? across < 8 : across < 54) continue;

      const middleOf = toScreen(part.x, part.z);
      if (
        middleOf.x < -80 ||
        middleOf.y < -20 ||
        middleOf.x > width + 80 ||
        middleOf.y > height + 20
      ) {
        continue;
      }

      const lit = over?.area.key === part.key;
      const size = part.town
        ? Math.max(10, Math.min(13, across / 6))
        : Math.max(10, Math.min(16, across / 11));

      context.font = `${part.town ? '600' : '500'} ${size}px system-ui, sans-serif`;

      const wide = Math.min(context.measureText(part.name).width, across || 200);
      const below = part.town ? middleOf.y + size : middleOf.y;
      if (!room(middleOf.x, below, wide + 8, size + 4)) continue;

      // A town is a place rather than a stretch of country, so it gets a mark
      // where it stands and its name under it.
      if (part.town) {
        context.fillStyle = '#f2e3c0';
        context.fillRect(middleOf.x - 2.5, middleOf.y - 2.5, 5, 5);
        context.strokeStyle = 'rgba(4, 20, 24, 0.8)';
        context.lineWidth = 1;
        context.strokeRect(middleOf.x - 3, middleOf.y - 3, 6, 6);
      }

      context.fillStyle = 'rgba(4, 20, 24, 0.75)';
      context.fillText(part.name, middleOf.x + 1, below + 1, across || 200);
      context.fillStyle = lit ? '#eaf6f8' : part.town ? '#f2e3c0' : '#d8e8ea';
      context.fillText(part.name, middleOf.x, below, across || 200);
    }

    // Places somebody has written down, and where they are headed. Drawn over
    // everything else, because they are the one thing on the map that is theirs.
    for (const marker of marks.markers) {
      if (marker.layer !== showing) continue;

      const spot = toScreen(marker.tx + 0.5, marker.tz + 0.5);
      if (spot.x < -20 || spot.y < -20 || spot.x > width + 20 || spot.y > height + 20) continue;

      context.fillStyle = marker.colour;
      context.strokeStyle = 'rgba(4, 20, 24, 0.85)';
      context.lineWidth = 1.5;

      if (marker.icon === 'star') {
        context.beginPath();
        for (let point = 0; point < 10; point++) {
          const away = point % 2 === 0 ? 6 : 2.6;
          const turn = (Math.PI / 5) * point - Math.PI / 2;
          const x = spot.x + Math.cos(turn) * away;
          const y = spot.y + Math.sin(turn) * away;
          if (point === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        }
        context.closePath();
        context.fill();
        context.stroke();
      } else {
        const size = marker.icon === 'dot' ? 5 : 7;
        context.fillRect(spot.x - size / 2, spot.y - size / 2, size, size);
        context.strokeRect(spot.x - size / 2 - 0.5, spot.y - size / 2 - 0.5, size + 1, size + 1);
      }

      if (perTile > 0.09) {
        context.font = '500 11px system-ui, sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'top';
        context.fillStyle = 'rgba(4, 20, 24, 0.8)';
        context.fillText(marker.name, spot.x + 1, spot.y + 8, 140);
        context.fillStyle = marker.colour;
        context.fillText(marker.name, spot.x, spot.y + 7, 140);
      }
    }

    // The party: where each of them is, and where they are headed together.
    for (const member of party.members) {
      if (!member.near || member.layer !== showing) continue;
      const spot = toScreen(member.tx + 0.5, member.tz + 0.5);

      context.fillStyle = '#c9a2ff';
      context.beginPath();
      context.arc(spot.x, spot.y, 3.5, 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = 'rgba(4, 20, 24, 0.9)';
      context.lineWidth = 1;
      context.stroke();

      context.font = '11px system-ui, sans-serif';
      context.textBaseline = 'top';
      context.fillStyle = 'rgba(4, 20, 24, 0.8)';
      context.fillText(member.name, spot.x + 1, spot.y + 6, 120);
      context.fillStyle = '#c9a2ff';
      context.fillText(member.name, spot.x, spot.y + 5, 120);
    }

    if (party.point && party.point.layer === showing) {
      const spot = toScreen(party.point.tx + 0.5, party.point.tz + 0.5);
      context.strokeStyle = '#c9a2ff';
      context.lineWidth = 2;
      context.beginPath();
      context.arc(spot.x, spot.y, 7, 0, Math.PI * 2);
      context.stroke();
    }

    if (marks.waypoint && marks.waypoint.layer === showing) {
      const spot = toScreen(marks.waypoint.tx + 0.5, marks.waypoint.tz + 0.5);

      context.strokeStyle = '#f2e3c0';
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(spot.x - 7, spot.y);
      context.lineTo(spot.x + 7, spot.y);
      context.moveTo(spot.x, spot.y - 7);
      context.lineTo(spot.x, spot.y + 7);
      context.stroke();

      context.beginPath();
      context.arc(spot.x, spot.y, 9, 0, Math.PI * 2);
      context.stroke();
    }

    areas.current = found;

    if (dragging) {
      const corner = toScreen(dragging.originX, dragging.originZ);
      context.strokeStyle = dragging.clash ? 'rgba(230, 90, 80, 0.95)' : BORDER_LIT;
      context.setLineDash([6, 4]);
      context.lineWidth = 2;
      context.strokeRect(corner.x, corner.y, dragging.width * perTile, dragging.height * perTile);
      context.setLineDash([]);
    }

    // Whoever is reading it, if they are on the layer being read.
    if (showing === (here?.layer ?? 0)) {
      const you = toScreen(view.where.x, view.where.z);
      context.fillStyle = YOU;
      context.fillRect(you.x - 4, you.y - 4, 8, 8);
      context.strokeStyle = '#04262a';
      context.lineWidth = 1.5;
      context.strokeRect(you.x - 4.5, you.y - 4.5, 9, 9);
    }
  };

  // Drawn again after every render, which is the only time anything about the
  // picture can have changed. A plain function and a plain effect: nothing to
  // memoize, so nothing for a memo to go stale against.
  useEffect(() => {
    draw();
  });

  /**
   * The words in the title bar, which follow the reader rather than the map.
   *
   * Read on a slow clock instead of while drawing, because setting state in the
   * middle of drawing is how a redraw becomes a loop.
   */
  useEffect(() => {
    const read = (): void => {
      const view = game.current?.mapView();
      if (!view) return;

      const here = view.plan.find((one) => one.id === view.region.id);
      setStandingOn(here?.layer ?? 0);
      setNamed(view.called || view.region.name);
      setWhere(`${Math.floor(view.where.x)}, ${Math.floor(view.where.z)}`);
    };

    read();
    const clock = window.setInterval(read, 250);
    return () => window.clearInterval(clock);
  }, [game]);

  // Redrawn when the panel changes size, since the picture is the panel's size.
  // A render is asked for rather than a draw, so the drawing all happens in the
  // one place.
  const [, resized] = useState(0);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === 'undefined') return;

    const watching = new ResizeObserver(() => resized((count) => count + 1));
    watching.observe(frame);
    return () => watching.disconnect();
  }, []);

  // ---------------------------------------------------------------------------
  // Reading the pointer
  // ---------------------------------------------------------------------------

  /** Where a point on the canvas is, in the world's own tiles. */
  const tileAt = (clientX: number, clientY: number): { tx: number; tz: number } | null => {
    const canvas = canvasRef.current;
    const view = game.current?.mapView();
    if (!canvas || !view) return null;

    const box = canvas.getBoundingClientRect();
    const perTile = scale > 0 ? scale : opensAt(box.width, box.height, view.region.width);
    const middle = centreFor(view, box.width, box.height, perTile);

    return {
      tx: Math.round(middle.x + (clientX - box.left - box.width / 2) / perTile),
      tz: Math.round(middle.z + (clientY - box.top - box.height / 2) / perTile),
    };
  };

  /** The smallest named place a point is inside, which is the useful answer. */
  const areaAt = (tx: number, tz: number): Area | null => {
    // The areas first: on a place that has them, they cover all of it, and they
    // are the answer to where somewhere is.
    for (const place of charted.current) {
      const bx = Math.floor((tx - place.originX) / place.chart.step);
      const bz = Math.floor((tz - place.originZ) / place.chart.step);
      if (bx < 0 || bz < 0 || bx >= place.chart.width || bz >= place.chart.height) continue;

      const held = place.chart.area[bz * place.chart.width + bx] ?? 0;
      if (held === 0) continue;

      const part = parts.current.get(place.id)?.find((one) => one.held === held);
      if (!part) continue;

      return {
        key: part.key,
        name: part.name,
        about: part.about,
        full: part.full,
        region: false,
        originX: place.originX,
        originZ: place.originZ,
        width: 0,
        height: 0,
      };
    }

    let best: Area | null = null;

    for (const area of areas.current) {
      if (area.region) continue;
      if (tx < area.originX || tx >= area.originX + area.width) continue;
      if (tz < area.originZ || tz >= area.originZ + area.height) continue;

      if (!best || area.width * area.height < best.width * best.height) best = area;
    }

    return best;
  };

  /**
   * The menu a right click opens: where to go, and what to write down.
   *
   * Clearing a waypoint is first when there is one, because somebody opening
   * this menu with a waypoint set is usually done with it.
   */
  const onRightClick = (event: React.MouseEvent<HTMLCanvasElement>): void => {
    event.preventDefault();

    const tile = tileAt(event.clientX, event.clientY);
    const box = event.currentTarget.getBoundingClientRect();
    if (!tile) return;

    setMenu({ x: event.clientX - box.left, y: event.clientY - box.top, tx: tile.tx, tz: tile.tz });
  };

  const setWaypoint = (tx: number, tz: number): void => {
    onMarks({ ...marks, waypoint: { tx, tz, layer: showing } });
    setMenu(null);
  };

  const addMarker = (tx: number, tz: number): void => {
    if (marks.markers.length >= MARKER_MAX) {
      setMenu(null);
      return;
    }

    const here = areaAt(tx, tz);
    const marker: MapMarker = {
      id: `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      name: markerName(here?.name ?? '', tx, tz),
      tx,
      tz,
      layer: showing,
      colour: MARKER_COLOURS[marks.markers.length % MARKER_COLOURS.length] ?? '#0cd5e3',
      icon: 'dot',
    };

    onMarks({ ...marks, markers: [...marks.markers, marker] });
    setMenu(null);
    setManaging(true);
  };

  const onMoveOver = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const tile = tileAt(event.clientX, event.clientY);
    if (!tile) return;

    const area = areaAt(tile.tx, tile.tz);
    const box = event.currentTarget.getBoundingClientRect();

    setOver(area ? { area, x: event.clientX - box.left, y: event.clientY - box.top } : null);
  };

  /** Dragging the map about, which is how anybody reads a map larger than a window. */
  const onGrab = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.button !== 0) return;

    const view = game.current?.mapView();
    if (!view) return;

    if (arranging && pickUp(event)) return;

    const from = { x: event.clientX, y: event.clientY };
    const box = event.currentTarget.getBoundingClientRect();
    const perTile = scale > 0 ? scale : opensAt(box.width, box.height, view.region.width);
    const middle = centreFor(view, box.width, box.height, perTile);

    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (moved: PointerEvent): void => {
      setAt({
        x: middle.x - (moved.clientX - from.x) / perTile,
        z: middle.z - (moved.clientY - from.y) / perTile,
      });
    };

    const drop = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', drop);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', drop);
  };

  /** Scrolling, which zooms about the pointer rather than about the middle. */
  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>): void => {
    const view = game.current?.mapView();
    const box = event.currentTarget.getBoundingClientRect();
    if (!view) return;

    const perTile = scale > 0 ? scale : opensAt(box.width, box.height, view.region.width);
    const middle = centreFor(view, box.width, box.height, perTile);

    const step = event.deltaY < 0 ? 1.2 : 1 / 1.2;
    const wanted = Math.max(FURTHEST, Math.min(CLOSEST, perTile * step));

    // The tile under the pointer stays under the pointer, which is what makes a
    // zoom feel like leaning in rather than being shoved.
    const px = event.clientX - box.left - box.width / 2;
    const py = event.clientY - box.top - box.height / 2;

    setAt({
      x: middle.x + px / perTile - px / wanted,
      z: middle.z + py / perTile - py / wanted,
    });
    setScale(wanted);
  };

  const zoomBy = (step: number): void => {
    const view = game.current?.mapView();
    const canvas = canvasRef.current;
    if (!view || !canvas) return;

    const perTile = scale > 0 ? scale : opensAt(canvas.width, canvas.height, view.region.width);

    setAt(at ?? { x: view.where.x, z: view.where.z });
    setScale(Math.max(FURTHEST, Math.min(CLOSEST, perTile * step)));
  };

  /**
   * Picking a region up off the map and putting it somewhere else.
   *
   * The whole of moving an island, for as long as an island is a region. What
   * is dragged is the outline: the ground itself is not redrawn until the world
   * says it has moved, because the world is the one that decides whether it can.
   */
  const pickUp = (event: React.PointerEvent<HTMLCanvasElement>): boolean => {
    const view = game.current?.mapView();
    const from = tileAt(event.clientX, event.clientY);
    if (!view || !from) return false;

    const held = view.plan.find(
      (one) =>
        one.width > 0 &&
        (one.layer ?? 0) === showing &&
        from.tx >= one.originX &&
        from.tx < one.originX + one.width &&
        from.tz >= one.originZ &&
        from.tz < one.originZ + one.height,
    );

    // The world is standing on the one being played, so it cannot be moved: the
    // server refuses it, and offering it would be offering nothing.
    if (!held || held.id === view.planHere) return false;

    event.preventDefault();
    const grip = { tx: from.tx - held.originX, tz: from.tz - held.originZ };

    const move = (moved: PointerEvent): void => {
      const now = tileAt(moved.clientX, moved.clientY);
      if (!now) return;

      const wanted = { ...held, originX: now.tx - grip.tx, originZ: now.tz - grip.tz };

      setDragging({
        id: held.id,
        originX: wanted.originX,
        originZ: wanted.originZ,
        width: held.width,
        height: held.height,
        clash: view.plan.some(
          (other) => other.id !== held.id && placesOverlap({ ...wanted, layer: showing }, other),
        ),
      });
    };

    const drop = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', drop);

      setDragging((landed) => {
        if (landed && !landed.clash) onMove?.(landed.id, landed.originX, landed.originZ);
        return null;
      });
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', drop);
    return true;
  };

  /**
   * Escape and the map key both put it away.
   *
   * The listener is put on once and reads the way out through a reference,
   * rather than being taken off and put back whenever the parent hands down a
   * new closure. That is not tidiness: a keypress that makes React re-render
   * partway through its own dispatch would take this listener off the window
   * and add a fresh one at the end of the list, and a listener added during a
   * dispatch is not called for it. The key would be swallowed, and only
   * sometimes, which is the worst way for a key to be broken.
   */
  const closing = useRef(onClose);

  useEffect(() => {
    closing.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' || event.key === 'm' || event.key === 'M') closing.current();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A layer nobody could be on is a layer nobody may read: you cannot see under
  // ground you are standing on top of, and above ground there is no under.
  const deepest = Math.min(0, standingOn);
  const canGoDown = showing > deepest;
  const canGoUp = showing < 0;

  // Only layers that something is actually on, so a world with no cellars does
  // not offer a picture of one.
  const layers = new Set((game.current?.mapView()?.plan ?? []).map((one) => one.layer ?? 0));
  layers.add(showing);

  return (
    <div
      className="world-map"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="world-map-inner">
        <div className="world-map-head">
          <b>{named}</b>
          <span className="hint">You are at {where}.</span>

          <div className="world-map-tools">
            {/* The layers, stacked the way they sit in the world: the one above
                faint because it is over your head, the one below dark because
                you cannot see through the floor you are standing on. */}
            <div className="world-map-layers">
              <button
                type="button"
                title="Read the layer above this one"
                disabled={!canGoUp}
                onClick={() => setLayer(showing + 1)}
              >
                ▲
              </button>
              <button
                type="button"
                title="Read the layer below this one"
                disabled={!canGoDown}
                onClick={() => setLayer(showing - 1)}
              >
                ▼
              </button>
            </div>

            <div className="world-map-stack">
              {layers.has(showing + 1) ? (
                <span className={canGoUp ? 'faint' : 'unreachable'}>{layerName(showing + 1)}</span>
              ) : null}
              <span className="now">{layerName(showing)}</span>
              {layers.has(showing - 1) ? (
                <span className="unreachable">{layerName(showing - 1)}</span>
              ) : null}
            </div>

            <button
              type="button"
              className={managing ? 'world-map-marks on' : 'world-map-marks'}
              title="The places you have written down"
              onClick={() => setManaging(!managing)}
            >
              Markers
            </button>

            <button type="button" title="Zoom out" onClick={() => zoomBy(1 / 1.4)}>
              −
            </button>
            <button type="button" title="Zoom in" onClick={() => zoomBy(1.4)}>
              +
            </button>
            <CloseButton onClose={onClose} />
          </div>
        </div>

        <div className="world-map-frame" ref={frameRef}>
          <canvas
            ref={canvasRef}
            style={{ cursor: arranging ? 'grab' : 'move' }}
            onPointerDown={onGrab}
            onPointerMove={onMoveOver}
            onPointerLeave={() => setOver(null)}
            onContextMenu={onRightClick}
            onWheel={onWheel}
          />

          {menu ? (
            <>
              <div className="world-map-shade" onPointerDown={() => setMenu(null)} />
              <div className="world-map-menu" style={{ left: menu.x, top: menu.y }}>
                <div className="menu-title">
                  {menu.tx}, {menu.tz}
                </div>

                {marks.waypoint ? (
                  <button
                    type="button"
                    className="menu-entry"
                    onClick={() => {
                      onMarks({ ...marks, waypoint: null });
                      setMenu(null);
                    }}
                  >
                    Clear Waypoint
                  </button>
                ) : null}

                <button
                  type="button"
                  className="menu-entry"
                  onClick={() => setWaypoint(menu.tx, menu.tz)}
                >
                  Add Waypoint
                </button>

                <button
                  type="button"
                  className="menu-entry"
                  onClick={() => addMarker(menu.tx, menu.tz)}
                >
                  Add Marker
                </button>
              </div>
            </>
          ) : null}

          {managing ? (
            <div className="world-map-markers">
              <div className="panel-head">
                <span>Markers</span>
                <CloseButton onClose={() => setManaging(false)} />
              </div>

              {marks.markers.length === 0 ? (
                <p className="hint">
                  Nothing written down yet. Right click anywhere on the map to add one.
                </p>
              ) : null}

              <div className="world-map-marker-list">
                {marks.markers.map((marker) => (
                  <div className="world-map-marker" key={marker.id}>
                    <input
                      type="text"
                      value={marker.name}
                      maxLength={32}
                      onChange={(event) =>
                        onMarks({
                          ...marks,
                          markers: marks.markers.map((one) =>
                            one.id === marker.id ? { ...one, name: event.target.value } : one,
                          ),
                        })
                      }
                    />

                    <div className="world-map-marker-colours">
                      {MARKER_COLOURS.map((colour) => (
                        <button
                          type="button"
                          key={colour}
                          className={marker.colour === colour ? 'on' : ''}
                          style={{ background: colour }}
                          title={`Draw it in this colour`}
                          onClick={() =>
                            onMarks({
                              ...marks,
                              markers: marks.markers.map((one) =>
                                one.id === marker.id ? { ...one, colour } : one,
                              ),
                            })
                          }
                        />
                      ))}
                    </div>

                    <select
                      value={marker.icon}
                      onChange={(event) =>
                        onMarks({
                          ...marks,
                          markers: marks.markers.map((one) =>
                            one.id === marker.id
                              ? { ...one, icon: event.target.value as MarkerIcon }
                              : one,
                          ),
                        })
                      }
                    >
                      {MARKER_ICONS.map((icon) => (
                        <option value={icon} key={icon}>
                          {icon}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      title="Take it off the map"
                      onClick={() =>
                        onMarks({
                          ...marks,
                          markers: marks.markers.filter((one) => one.id !== marker.id),
                        })
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {over ? (
            <div
              className="world-map-tip"
              style={{ left: over.x + 14, top: over.y + 14 }}
              role="tooltip"
            >
              <b>{over.area.name}</b>
              {over.area.full && over.area.full !== over.area.name ? <i>{over.area.full}</i> : null}
              {over.area.about ? <span>{over.area.about}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
